import React, { useEffect, useRef, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../../store/hooks/StoreHooks";
import Accordion from "../../../../common/accordion";
import ButtonGroup from "../../../../common/button-group";
import SwitchButton from "../../../../common/switch-button";
import Modal from "../../../../common/Modal";
import { LABELS } from "../../../../../constants/labels/jetlift/jetlift.labels";
import TextInput from "../../../../common/textinput";
import * as XLSX from 'xlsx';
import useJetliftCollapseFacade from "../../../../../facade/jetlift/collapse/collapse.facade";
import useJetliftWellSurveyFacade from "../../../../../facade/jetlift/jetlift-output/wellSurvey.facade";
import { setMaxDLSAboveKOP, setTransitionZone } from "../../../../../store/jetlift/jetlift-output/WellSurveySlice";
import WellSurveyPlot1 from "../WellSurveyPlot1";
import WellSurveyPlot2 from "../WellSurveyPlot2";
import WellSurveyDataTable from "../WellSurveyDataTable";
import Textarea from "../../../../common/textarea";
import "./styles.scss";
import { RootState } from "../../../../../store/ConfigureStore";
import html2canvas from "html2canvas";

const PerformanceCurves: React.FC = () => {
  const dispatch = useAppDispatch();
  const { performanceCurvesCollapse } = useAppSelector(
    (state) => state?.jetlift?.collapse
  );
  const {
    wellSurveyData,
    loading,
    error,
    KOPOverride,
    MaxDLSAboveKOP,
    DepthOfMaxDLS,
    TransitionZone,
    TailPipeLength,
    TailPipeBottom,
    ESPLength,
    BottomOfESPSensor,
    TailPipeDesander,
    wellSurvey,
    showWellDataInMainReport,
    isWellSurveyDataPersisted,
  } = useAppSelector((state) => state?.jetlift?.wellSurvey);
  const notes = wellSurvey?.notes;
  const { selectedProject } = useAppSelector((state) => state?.myProjects);
  const { customer, well } = useAppSelector(
    (state) => state?.createProject || {}
  );
  const { tubingInput } = useAppSelector((state) => state?.jetlift?.tubing);
  const { wellInput } = useAppSelector((state) => state?.jetlift?.well);
  const { updatePerformanceCurvesCollapse } = useJetliftCollapseFacade();
  const {
    importWellSurveyData,
    getExistingWellSurveyData,
    clearWellSurveyData,
    updateWellNotes,
    updateKOPOverride,
    updateWellDataInMainReportFlag,
  } = useJetliftWellSurveyFacade();

  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | number | null>(null);
  const [showClearModal, setShowClearModal] = useState(false);

  const [inputValues, setInputValues] = useState({
    startCell: { md: "", inc: "", azi: "" },
    endCell: { md: "", inc: "", azi: "" },
    sheetName: "",
  });
  const [isAzi, setIsAzi] = useState<boolean>(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errors, setErrors] = useState({
    startCell: { md: false, inc: false, azi: false },
    endCell: { md: false, inc: false, azi: false },
    sheetName: false,
    file: false,
  });
  const surveyChartRef = useRef<HTMLDivElement | null>(null);
  const surveyChart2Ref = useRef<HTMLDivElement | null>(null);
  const lastFetchedProjectIdRef = useRef<string | null>(null);
  const transitionZone = useAppSelector((state: RootState) => state.jetlift.wellSurvey.TransitionZone ?? 0);

  useEffect(() => {
    const projectId = selectedProject?.projectID || selectedProject?.caseID || null;
    if (!projectId) {
      lastFetchedProjectIdRef.current = null;
      return;
    }
    if (lastFetchedProjectIdRef.current === projectId) return;
    lastFetchedProjectIdRef.current = projectId;
    getExistingWellSurveyData(projectId);
  }, [selectedProject?.projectID, selectedProject?.caseID]);

  // Clear form and reset selection after successful import
  useEffect(() => {
    if (wellSurveyData && wellSurveyData.length > 0 && !loading) {
      setInputValues({
        startCell: { md: "", inc: "", azi: "" },
        endCell: { md: "", inc: "", azi: "" },
        sheetName: "",
      });
      setSelectedFile(null);
      setErrors({
        startCell: { md: false, inc: false, azi: false },
        endCell: { md: false, inc: false, azi: false },
        sheetName: false,
        file: false,
      });
      // Clear selected row when new data is imported
      setSelectedRow(null);
      setSelectedRowId(null);
    }
  }, [wellSurveyData, loading]);

  // Reset selected row when data is cleared
  useEffect(() => {
    if (!wellSurveyData || wellSurveyData.length === 0) {
      setSelectedRow(null);
      setSelectedRowId(null);
    }
  }, [wellSurveyData]);

  // Restore selected row based on saved KOP Override value - REMOVED AUTO-SELECTION
  // KOP should only be set manually by clicking a row, not automatically on data load

  // Calculate DLS values when KOP is manually selected
  useEffect(() => {
    if (wellSurveyData && wellSurveyData.length > 0 && KOPOverride) {
      const timer = setTimeout(() => {
        const dlsValues = wellSurveyData.map((item: any) => item.dls || 0);
        const kopIndex = wellSurveyData.findIndex((item: any) => item.md >= KOPOverride);

        if (kopIndex > 0) {
          const dlsAboveKOP = dlsValues.slice(0, kopIndex);
          const maxDLS = Math.max(...dlsAboveKOP);
          const maxDLSIndex = dlsAboveKOP.indexOf(maxDLS);
          const depthOfMaxDLS = wellSurveyData[maxDLSIndex]?.md || 0;
          dispatch(setMaxDLSAboveKOP(maxDLS));
          dispatch({ type: 'wellSurvey/setTailPipeLength', payload: depthOfMaxDLS });
          const tailPipeBottomDepth = (BottomOfESPSensor || 0) + (TailPipeDesander || 0);
          dispatch({ type: 'wellSurvey/setTailPipeBottom', payload: tailPipeBottomDepth });
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [wellSurveyData, KOPOverride, BottomOfESPSensor, TailPipeDesander, dispatch]);

  const handleRowClick = (row: any) => {
    setSelectedRow(row);
    setSelectedRowId(row.id);
    updateKOPOverride(row.md);
  };

  const handleWellNotesChange = (event: any) => {
    updateWellNotes(event.target.value);
  };

  const handleRadioChange = (value: boolean) => {
    setIsAzi(value);
  };

  const handleInputChange = (
    section: "startCell" | "endCell" | "sheetName",
    value: string,
    field?: "md" | "inc" | "azi"
  ) => {
    if (section === "sheetName") {
      setInputValues((prev) => ({ ...prev, sheetName: value }));
      setErrors((prev) => ({ ...prev, sheetName: false }));
    } else if (field) {
      setInputValues((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
      setErrors((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: false },
      }));
    }
  };

  const handleImport = () => {
    const newErrors = {
      startCell: {
        md: !inputValues.startCell.md.trim(),
        inc: !inputValues.startCell.inc.trim(),
        azi: !inputValues.startCell.azi.trim(),
      },
      endCell: {
        md: !inputValues.endCell.md.trim(),
        inc: !inputValues.endCell.inc.trim(),
        azi: !inputValues.endCell.azi.trim(),
      },
      sheetName: !inputValues.sheetName.trim(),
      file: !selectedFile,
    };

    setErrors(newErrors);

    const hasErrors =
      Object.values(newErrors.startCell).some(Boolean) ||
      Object.values(newErrors.endCell).some(Boolean) ||
      newErrors.sheetName ||
      newErrors.file;

    if (hasErrors) return;

    const formData = new FormData();
    formData.append("ServeyFile", selectedFile!);
    formData.append("SheetName", inputValues.sheetName);
    formData.append("MD.StartCell", inputValues.startCell.md);
    formData.append("MD.EndCell", inputValues.endCell.md);
    formData.append("INC.StartCell", inputValues.startCell.inc);
    formData.append("INC.EndCell", inputValues.endCell.inc);
    formData.append("IsAZI", isAzi.toString());
    formData.append("AziOrDLS.StartCell", inputValues.startCell.azi);
    formData.append("AziOrDLS.EndCell", inputValues.endCell.azi);

    importWellSurveyData(formData);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(event.target.files?.[0] || null);
  };

  const handleClearData = () => {
    setShowClearModal(true);
  };

  const confirmClearData = () => {
    if (selectedProject?.projectID || selectedProject?.caseID) {
      clearWellSurveyData(selectedProject?.projectID || selectedProject?.caseID);
    } else {
      clearWellSurveyData();
    }
    setShowClearModal(false);
  };

  const cancelClearData = () => {
    setShowClearModal(false);
  };

  const handleToggleButton = (checked: boolean) => {
    updateWellDataInMainReportFlag(checked);
  };

  const exportToExcel = () => {
    try {
      // Extract data from wellSurveyData
      const exportData = wellSurveyData.map((row: { md: number; inc: number; dls: number }) => ({
        MD: row.md,
        INC: row.inc,
        DLS: row.dls
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Survey Data');

      // Generate Excel file
      XLSX.writeFile(wb, 'WellSurveyData.xlsx');

    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const handleWellSurveyChart1Download = () => {
    if (!surveyChartRef.current) {
      return;
    }
    if (surveyChartRef.current) {
      html2canvas(surveyChartRef.current, {
        backgroundColor: "#253040",
      }).then((canvas) => {
        const imgUrl = canvas.toDataURL("image/png");

        const link = document.createElement("a");
        link.href = imgUrl;
        link.download = "Survey Performance Plots.png";
        link.click();
      });
    }
  };

  const handleWellSurveyChart2Download = () => {
    if (!surveyChart2Ref.current) {
      return;
    }
    if (surveyChart2Ref.current) {
      html2canvas(surveyChart2Ref.current, {
        backgroundColor: "#253040",
      }).then((canvas) => {
        const imgUrl = canvas.toDataURL("image/png");

        const link = document.createElement("a");
        link.href = imgUrl;
        link.download = "Survey Performance Plots.png";
        link.click();
      });
    }
  };

  return (
    <div>
      <Modal
        visible={showClearModal}
        onClose={cancelClearData}
        title="Confirm Clear Data"
        showButtons={true}
        yesButton="OK"
        noButton="Cancel"
        onConfirm={confirmClearData}
      >
        <p className="text-neutral-25">
          Are you sure you want to clear all well survey data?
        </p>
      </Modal>
      <Accordion
        title={LABELS?.performanceCurves}
        isOpen={performanceCurvesCollapse}
        onToggle={() =>
          updatePerformanceCurvesCollapse(!performanceCurvesCollapse)
        }
        content={
          <div className='flex flex-col gap-[24px] p-4 w-full h-full'>
            <div className='flex justify-between items-center'>
              <p className='text-neutral-25'>{LABELS?.importFromExcel}</p>
              <div className='flex gap-2'>
                <ButtonGroup
                  buttonOptions={[
                    {
                      id: 1,
                      icon: "",
                      label: `${LABELS?.btnImport}`,
                      type: `${LABELS?.typePrimary}`,
                    },
                  ]}
                  handleClick={handleImport}
                />

              </div>
            </div>

            {loading && (
              <div className='text-neutral-25 text-sm'>Loading...</div>
            )}
            {error && (
              <div className='text-red-500 text-sm'>Error: {error}</div>
            )}

            <div className='relative border-[1px] rounded-[8px] border-darkcolor-400'>
              <table className='table-fixed w-full rounded-[8px] outline-[1px]'>
                <thead style={{ background: "#253040" }}>
                  <tr className='border-b-[1px] border-darkcolor-400'>
                    <th className='w-1/4 pl-[16px] pr-[12px] py-[16px] text-[12px] text-neutral-25 text-left'>
                      {LABELS?.excelInputs}
                    </th>
                    <th className='w-1/4 pr-[12px] py-[16px] text-[12px] text-neutral-25 text-left'>
                      {LABELS?.md}
                    </th>
                    <th className='w-1/4 pr-[12px] py-[16px] text-[12px] text-neutral-25 text-left'>
                      {LABELS?.inc}
                    </th>
                    <th className='w-1/4 pr-[12px] py-[16px] text-[12px] text-neutral-25 text-left'>
                      <div className='flex gap-[16px]'>
                        <label className='flex items-center gap-2 text-neutral-25'>
                          <input
                            type='radio'
                            name='aziOrDls'
                            value='azi'
                            checked={isAzi}
                            onChange={() => handleRadioChange(true)}
                            className='accent-primary-500'
                          />
                          {LABELS?.azi}
                        </label>
                        <label className='flex items-center gap-2 text-neutral-25'>
                          <input
                            type='radio'
                            name='aziOrDls'
                            value='dls'
                            checked={!isAzi}
                            onChange={() => handleRadioChange(false)}
                            className='accent-primary-500'
                          />
                          {LABELS?.dls}
                        </label>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className='border-b-[1px] border-darkcolor-400'>
                    <td className='w-1/4 px-[24px] py-[9px] text-[14px] text-neutral-25'>
                      {LABELS?.startCellLocation}
                    </td>
                    <td className='w-1/4 pr-[12px] py-[9px]'>
                      <TextInput
                        name='md'
                        placeholder=''
                        value={inputValues.startCell.md}
                        onChange={(e: any) =>
                          handleInputChange("startCell", e.target.value, "md")
                        }
                        bgColor='bg-darkcolor-800'
                        error={errors.startCell.md ? "Required" : ""}
                      />
                    </td>
                    <td className='w-1/4 pr-[12px] py-[9px]'>
                      <TextInput
                        name='inc'
                        placeholder=''
                        value={inputValues.startCell.inc}
                        onChange={(e: any) =>
                          handleInputChange("startCell", e.target.value, "inc")
                        }
                        bgColor='bg-darkcolor-800'
                        error={errors.startCell.inc ? "Required" : ""}
                      />
                    </td>
                    <td className='w-1/4 pr-[12px] py-[9px]'>
                      <TextInput
                        name='azi'
                        placeholder=''
                        value={inputValues.startCell.azi}
                        onChange={(e: any) =>
                          handleInputChange("startCell", e.target.value, "azi")
                        }
                        bgColor='bg-darkcolor-800'
                        error={errors.startCell.azi ? "Required" : ""}
                      />
                    </td>
                  </tr>
                  <tr className='border-b-[1px] border-darkcolor-400'>
                    <td className='w-1/4 px-[24px] py-[9px] text-[14px] text-neutral-25'>
                      {LABELS?.endCellLocation}
                    </td>
                    <td className='w-1/4 pr-[12px] py-[9px]'>
                      <TextInput
                        name='endcell-md'
                        placeholder=''
                        value={inputValues.endCell.md}
                        onChange={(e: any) =>
                          handleInputChange("endCell", e.target.value, "md")
                        }
                        bgColor='bg-darkcolor-800'
                        error={errors.endCell.md ? "Required" : ""}
                      />
                    </td>
                    <td className='w-1/4 pr-[12px] py-[9px]'>
                      <TextInput
                        name='endcell-inc'
                        placeholder=''
                        value={inputValues.endCell.inc}
                        onChange={(e: any) =>
                          handleInputChange("endCell", e.target.value, "inc")
                        }
                        bgColor='bg-darkcolor-800'
                        error={errors.endCell.inc ? "Required" : ""}
                      />
                    </td>
                    <td className='w-1/4 pr-[12px] py-[9px]'>
                      <TextInput
                        name='endcell-azi'
                        placeholder=''
                        value={inputValues.endCell.azi}
                        onChange={(e: any) =>
                          handleInputChange("endCell", e.target.value, "azi")
                        }
                        bgColor='bg-darkcolor-800'
                        error={errors.endCell.azi ? "Required" : ""}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className='w-1/4 px-[24px] py-[9px] text-[14px] text-neutral-25'>
                      {LABELS?.sheetName}
                    </td>
                    <td className='w-1/4 pr-[12px] py-[9px]'>
                      <TextInput
                        name='sheetname'
                        placeholder=''
                        value={inputValues.sheetName}
                        onChange={(e: any) =>
                          handleInputChange("sheetName", e.target.value)
                        }
                        bgColor='bg-darkcolor-800'
                        error={errors.sheetName ? "Required" : ""}
                      />
                    </td>
                    <td colSpan={2} className='w-2/4 pr-[12px] py-[9px]'>
                      <label
                        htmlFor='file-upload'
                        className={`cursor-pointer bg-darkcolor-800 flex items-center w-full rounded-[8px] px-4 py-2 ${errors.file ? '!border-red-500' : 'border-darkcolor-400'} border-[1px] hover:border-primary-500 focus:ring-primary-500 focus-focus-within:border-primary-500`}
                        style={errors.file ? { borderColor: '#ef4444' } : {}}
                      >
                        <input
                          name='file'
                          id='file-upload'
                          type='file'
                          accept='.xlsx,.xls'
                          onChange={handleFileChange}
                          className='hidden'
                        />
                        <span className='flex-1 text-white truncate'>
                          {selectedFile
                            ? selectedFile.name
                            : "No file selected"}
                        </span>
                        <span
                          className='ml-4 px-[10px] py-[4px] rounded-[8px] 
                                                border-[1px] border-darkcolor-400 text-sm font-normal 
                                                hover:bg-darkcolor-500 hover:border-darkcolor-300 
                                                font-bold text-base text-white'
                        >
                          Choose file
                        </span>
                      </label>
                      {errors.file && (
                        <div style={{ color: '#ef4444' }} className='text-xs mt-1'>Required</div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {wellSurveyData && wellSurveyData.length > 0 && (
              <>
                <div className='flex justify-between items-center mb-4'>
                  <p className='text-neutral-25'>Survey data</p>
                  <div className='flex gap-[8px]'>
                    <ButtonGroup
                      buttonOptions={[
                        {
                          id: 1,
                          icon: "",
                          label: "Export to Excel",
                          type: "",
                        },
                      ]}
                      handleClick={exportToExcel}
                    />
                    <ButtonGroup
                      buttonOptions={[
                        {
                          id: 2,
                          icon: "",
                          label: "Clear Data",
                          type: "secondary",
                          tooltip: isWellSurveyDataPersisted
                            ? "This will delete data from database"
                            : "This will clear imported data"
                        },
                      ]}
                      handleClick={handleClearData}
                    />
                  </div>
                </div>

                <WellSurveyDataTable
                  onRowClick={handleRowClick}
                  selectedRow={selectedRow}
                  selectedRowId={selectedRowId}
                  kopOverride={KOPOverride || undefined}
                />

                <div className='text-neutral-25 mb-4 mt-8'>
                  Setting Depth Survey
                </div>

                <div className='flex border-[1px] border-darkcolor-400 rounded-[8px] w-full mb-8'>
                  <div className='flex flex-col w-1/2'>
                    <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                      <label className='text-neutral-25 text-[14px] font-normal'>
                        KOP
                      </label>
                      <label className='text-neutral-25'>
                        {KOPOverride || 0}
                      </label>
                    </div>
                    <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                      <label className='text-neutral-25'>
                        Max DLS Above KOP
                      </label>
                      <label className='text-neutral-25'>
                        {MaxDLSAboveKOP || 0}
                      </label>
                    </div>
                    <div className='flex justify-between items-center py-[9px] px-[24px] w-full'>
                      <label className='text-neutral-25'>
                        Depth of Max DLS
                      </label>
                      <label className='text-neutral-25'>
                        {TailPipeLength || 0}
                      </label>
                    </div>
                  </div>
                  <div className='flex flex-col w-1/2'>
                    <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                      <label className='text-neutral-25 text-[14px] font-normal'>
                        Transition Zone
                      </label>
                      <label className='text-neutral-25'>
                        {TransitionZone || 0}
                      </label>
                    </div>
                    <div className='flex justify-between items-center py-[9px] px-[24px] w-full border-b-[1px] border-darkcolor-400'>
                      <label className='text-neutral-25 text-[14px] font-normal'>
                        Tubing Size
                      </label>
                      <label className='flex justify-end flex-wrap text-neutral-25'>
                        {tubingInput?.tubing1_tubingOD ? (
                          Array.isArray(tubingInput?.tubingODList) ?
                            tubingInput.tubingODList.find((item: any) => +item?.value === +tubingInput.tubing1_tubingOD!)?.label || tubingInput.tubing1_tubingOD :
                            tubingInput.tubing1_tubingOD
                        ) : ""}{" "}
                        &nbsp;
                        {tubingInput?.tubing1_tubingWeight ? (
                          Array.isArray(tubingInput?.tubing1WeightList) ?
                            tubingInput.tubing1WeightList.find((item: any) => +item?.value === +tubingInput.tubing1_tubingWeight!)?.label || tubingInput.tubing1_tubingWeight :
                            tubingInput.tubing1_tubingWeight
                        ) : ""}{" "}
                        &nbsp;
                      </label>
                    </div>
                    <div className='flex justify-between items-center py-[9px] px-[24px] w-full'>
                      <label className='text-neutral-25'>Casing Size</label>
                      <label className='flex justify-end flex-wrap text-neutral-25'>
                        {tubingInput?.casingOD ? (
                          Array.isArray(tubingInput?.casingODList) ?
                            tubingInput.casingODList.find((item: any) => +item?.value === +tubingInput.casingOD!)?.label || tubingInput.casingOD :
                            tubingInput.casingOD
                        ) : ""}{" "}
                        &nbsp;
                        {tubingInput?.casingWeight ? (
                          Array.isArray(tubingInput?.casingWeightList) ?
                            tubingInput.casingWeightList.find((item: any) => +item?.value === +tubingInput.casingWeight!)?.label || tubingInput.casingWeight :
                            tubingInput.casingWeight
                        ) : ""}{" "}
                        &nbsp;
                      </label>
                    </div>
                  </div>
                </div>

                {KOPOverride && KOPOverride > 0 && (
                  <div className='flex flex-col w-full gap-8'>
                    <div className='flex flex-col w-full'>
                      <div className="flex justify-between items-center">
                        <div className='flex text-neutral-25 mb-4 whitespace-nowrap'>
                          Well Survey Plot 1
                        </div>
                        <div
                          className='text-neutral-25 font-bold'
                          style={{ width: "100%" }}
                        >
                          <button
                            onClick={handleWellSurveyChart1Download}
                            className='ml-[16px] mb-4 border-[1px] border-solid border-[#4A5463] py-[8px] px-[14px] inline-flex justify-center items-center gap-[8px] rounded-[8px] bg-transparent text-neutral-25 float-right'
                          >
                            Export
                          </button>
                        </div>
                      </div>
                      <div className='flex justify-center items-start w-full' ref={surveyChartRef}>
                        <WellSurveyPlot1
                          selectedRow={selectedRow}
                          surveyData={wellSurveyData}
                          maxDls={Math.max(
                            ...wellSurveyData.map((o: any) => o.dls || 0)
                          )}
                          maxMd={Math.max(
                            ...wellSurveyData.map((item: any) => item.md)
                          )}
                          customerName={customer?.toString() || "--"}
                          wellName={well?.toString() || "--"}
                          surveyMetaData={{
                            kop: KOPOverride || 0,
                            topOfEspPump: wellInput?.pumpSetting || 0,
                            bottomOfESPSensor: BottomOfESPSensor || 0,
                            tailPipeDesander: TailPipeDesander || 0,
                            tailPipeBottom: (BottomOfESPSensor || 0) + (TailPipeDesander || 0),
                          }}
                        />
                      </div>
                    </div>

                    <div className='w-full my-8'>
                      <Textarea
                        label='Notes'
                        value={notes ?? ""}
                        maxLength={1000}
                        onChange={handleWellNotesChange}
                        hasOverflow={true}
                      />
                    </div>

                    <div className='flex flex-col w-full'>
                      <div className="flex justify-between items-center">
                        <div className='flex text-neutral-25 mb-4'>
                          Well Survey Plot 2
                        </div>
                        <div className='text-neutral-25 font-bold'>
                          <button
                            onClick={handleWellSurveyChart2Download}
                            className='ml-[16px] mb-4 border-[1px] border-solid border-[#4A5463] py-[8px] px-[14px] inline-flex justify-center items-center gap-[8px] rounded-[8px] bg-transparent text-neutral-25 whitespace-nowrap min-w-max float-right'
                          >
                            Export
                          </button>
                        </div>
                      </div>
                      <div className='flex justify-center items-start w-full' ref={surveyChart2Ref}>
                        <WellSurveyPlot2
                          selectedRow={selectedRow}
                          surveyData={wellSurveyData}
                          maxDls={Math.max(
                            ...wellSurveyData.map((o: any) => o.dls || 0)
                          )}
                          maxMd={Math.max(
                            ...wellSurveyData.map((item: any) => item.md)
                          )}
                          customerName={customer?.toString() || "--"}
                          wellName={well?.toString() || "--"}
                          surveyMetaData={{
                            kop: KOPOverride || 0,
                            topOfEspPump: wellInput?.pumpSetting || 0,
                            bottomOfESPSensor: BottomOfESPSensor || 0,
                            tailPipeDesander: TailPipeDesander || 0,
                            tailPipeBottom: (BottomOfESPSensor || 0) + (TailPipeDesander || 0),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        }
      />
    </div>
  );
};

export default PerformanceCurves;
